import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    Input,
    OnInit,
    signal,
    ViewChild
} from "@angular/core";

import { PostViewType } from "@app/shared/types/post.view.type";
import { RemovePostController } from "@arch/presentation/controllers/remove-post.controller";
import { MenuOption } from "@app/shared/components/dsn/button-menu-chips/button-menu-chips.component";
import { CompanyEntity, VisualizationEntity } from "@arch/domain/entity";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { InsertPostController } from "@arch/presentation/controllers/insert-post.controller";
import { MatDialog } from "@angular/material/dialog";
import { DateUtils } from "@arch/_shared/utils";
import { StringUtils } from "@arch/_shared/utils/String.utils";
import { DialogConfirmationMobileComponent } from "../dsn/dialog-confirmation-mobile/dialog-confirmation-mobile.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { ChatMessageComponent } from "../chat-message/chat-message.component";
import { DeviceService } from "@app/shared/services/device.service";
import { JwtAuthService } from "@app/shared/services/jwt-auth.service";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ReportEntity, PostEntity } from "@arch/domain/entity";
import { GeneratorIdUtils } from "@arch/_shared/utils/generator-id.utils";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ContactService } from "@app/shared/services/contact.service";

@Component({
    selector: "app-comments-post",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        ChatMessageComponent,
        CommonModule,
        ReactiveFormsModule
    ],
    templateUrl: "./comments-post.component.html",
    styleUrl: "./comments-post.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommentsPostComponent implements OnInit, AfterViewInit {
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: true }) options: VisualizationOptionsType;
    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: false }) isMobileComment: boolean = false;
    @ViewChild("postsContainer") postsContainer: ElementRef;

    readonly dialog = inject(MatDialog);
    readonly cdk = inject(ChangeDetectorRef);
    readonly insertPostController = inject(InsertPostController);
    readonly removePostController = inject(RemovePostController);
    readonly jwtService = inject(JwtAuthService);
    readonly eventsService = inject(EventsService);
    private readonly deviceService = inject(DeviceService);
    private readonly contactService = inject(ContactService);

    public isMobile = this.deviceService.getIsMobile();
    public minHeightPerPost: number = 49;
    public minHeight: string = "0px";
    public objectKeys = Object.keys;
    public currentMonth = new Date().getUTCMonth();
    public monthsChat: number[] = [];
    public loading = true;
    public loadingComment: boolean = false;
    public currentUserLike: PostEntity | null = null;
    public disableOnInit: boolean = true;
    public contact = this.contactService.get();

    readonly _posts = signal<{ [key: string]: PostViewType[] }>({});

    public get posts(): { [key: string]: PostViewType[] } {
        return this._posts();
    }

    public likeOptions: MenuOption[] = [
        { label: "Gostei do resultado", value: "liked" },
        { label: "Gostei do indicador", value: "liked" },
        { label: "Gostei da análise", value: "liked" },
        { label: "Gostei, mas tenho dúvidas", value: "liked" }
    ];
    public unlikeOptions: MenuOption[] = [
        { label: "Não gostei do resultado", value: "disliked" },
        { label: "Não gostei do indicador", value: "disliked" },
        { label: "Não gostei da análise", value: "disliked" },
        { label: "Tenho dúvidas", value: "doubts" }
    ];
    public commentForm: FormGroup = new FormGroup({
        comment: new FormControl("")
    });

    constructor() {}

    ngOnInit(): void {
        this.modelerPosts();
        this.updateMinHeight();
        this.checkCurrentUserLike();
        this.updateOptionsState();
    }

    ngAfterViewInit(): void {
        this.scrollToBottom();
        setTimeout(() => {
            this.loading = false;
            this.disableOnInit = false;
            this.cdk.detectChanges();
        }, 100);
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            if (this.postsContainer) {
                const element = this.postsContainer.nativeElement;
                element.scrollTo({
                    top: element.scrollHeight,
                    behavior: "smooth"
                });
            }
        });
    }

    private modelerPosts(): void {
        const groupedPosts: { [key: string]: PostViewType[] } = {};
        const posts = this.visualization.getPostsSortedByDate().reverse();
        posts
            .filter((post) => !post.props.isLike)
            .forEach((post) => {
                this.monthsChat.push(post.commentDate.getUTCMonth());
                const monthYear = DateUtils.dateToString(
                    post.commentDate,
                    "MMMM 'de' yyyy"
                );
                if (!groupedPosts[monthYear as any]) {
                    groupedPosts[monthYear as any] = [];
                }
                const postView: PostViewType = {
                    _id: post.props._id,
                    initials: StringUtils.getInitials(post.props.u.n),
                    message: post.props.comment.msg,
                    date: DateUtils.dateToString(
                        post.commentDate,
                        "dd 'de' MMMM' às' HH:mm"
                    ),
                    userName: post.props.u.n,
                    userId: post.props.u._id
                };
                groupedPosts[monthYear].push(postView);
            });
        this._posts.set(groupedPosts);
        this.updateMinHeight();
    }

    private updateMinHeight(): void {
        const totalPosts = Object.values(this.posts).reduce(
            (acc, arr) => acc + arr.length,
            0
        );
        this.minHeight = `${totalPosts * this.minHeightPerPost}px`;
        this.cdk.markForCheck();
    }

    public onDeletePost(post: PostViewType, key: string): void {
        const dialogRef = this.dialog.open(DialogConfirmationMobileComponent, {
            width: "180px",
            minWidth: "180px",
            data: {
                options: [
                    { label: "Excluir", value: true, alert: true },
                    { label: "Cancelar", value: false }
                ]
            }
        });
        dialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                await this.removePost(post, key);
                const posts = this.visualization?.posts || [];
                const postIndex = posts.findIndex(
                    (p) => p.props._id === post._id
                );
                if (postIndex >= 0) {
                    this.visualization.posts = [
                        ...posts.slice(0, postIndex),
                        ...posts.slice(postIndex + 1)
                    ];
                }
                this.updateMinHeight();
            }
        });
    }

    private async removePost(post: PostViewType, key: string): Promise<void> {
        const postEntity = this.visualization.posts.find(
            (p) => p.props._id === post._id
        );
        if (!postEntity) return;

        const currentPosts = { ...this.posts };
        currentPosts[key] = currentPosts[key].filter((p) => p._id !== post._id);
        this._posts.set(currentPosts);
        this.updateMinHeight();

        this.removePostController.execute({
            postId: postEntity.props._id,
            userId: postEntity.props.u._id
        });

        // Se foi removido um like, atualiza o estado
        if (postEntity.props.isLike) {
            this.checkCurrentUserLike();
            this.updateOptionsState();
        }
    }

    private checkCurrentUserLike(): void {
        if (
            this.visualization.postsLike &&
            this.visualization.postsLike.length > 0
        ) {
            this.currentUserLike =
                this.visualization.postsLike.find(
                    (like) => like.props.u._id === this.contact.props._id
                ) || null;
        } else {
            this.currentUserLike = null;
        }
    }

    public hasUserLiked(): boolean {
        return this.currentUserLike !== null;
    }

    public getUserLikeOption(): string {
        if (this.currentUserLike && this.currentUserLike.props.comment) {
            return this.currentUserLike.props.comment.msg || "";
        }
        return "";
    }

    public getUserLikeType(): string {
        if (this.currentUserLike && this.currentUserLike.props.comment) {
            return this.currentUserLike.props.comment.optionLike || "";
        }
        return "";
    }

    public isLikeOption(optionLabel: string): boolean {
        return this.likeOptions.some((option) => option.label === optionLabel);
    }

    public isDislikeOption(optionLabel: string): boolean {
        return this.unlikeOptions.some(
            (option) => option.label === optionLabel
        );
    }

    public getSelectedLikeOption(): MenuOption | null {
        if (!this.hasUserLiked()) return null;

        const userLikeOption = this.getUserLikeOption();
        const allOptions = [...this.likeOptions, ...this.unlikeOptions];
        return (
            allOptions.find((option) => option.label === userLikeOption) || null
        );
    }

    private updateOptionsState(): void {
        const userLikeOption = this.getUserLikeOption(); // Para o balão

        // Marca as opções de like baseado no comment.msg
        this.likeOptions = this.likeOptions.map((option) => ({
            ...option,
            selected: option.label === userLikeOption
        }));

        // Marca as opções de dislike baseado no comment.msg
        this.unlikeOptions = this.unlikeOptions.map((option) => ({
            ...option,
            selected: option.label === userLikeOption
        }));
    }

    public async onSelectLikeOption(option: MenuOption): Promise<void> {
        try {
            // Se o usuário já deu like, remove o like anterior
            if (this.currentUserLike) {
                await this.removePost(
                    {
                        _id: this.currentUserLike.props._id,
                        message: this.currentUserLike.props.comment.msg,
                        userId: this.currentUserLike.props.u._id,
                        userName: this.currentUserLike.props.u.n,
                        date: "",
                        initials: ""
                    },
                    ""
                );
                // Atualiza o estado após remover o like anterior
                this.checkCurrentUserLike();
            }

            // Insere o novo like
            const postId = GeneratorIdUtils.execute();
            this.insertPostController.execute({
                _id: postId,
                accountingId: this.company.props.accountingId,
                companyId: this.company.props._id,
                comment: {
                    msg: option.label,
                    t: new Date(),
                    optionLike: option.value
                },
                scenarioId: this.options.scenario.props._id,
                periodDate: this.options.endDate,
                user: {
                    _id: this.contact.props._id,
                    n: this.contact.props.name,
                    isContact: this.contact.isContact
                },
                visualizationId: this.visualization.props._id,
                reportId: this.report.props._id,
                reportName: this.report.props.name,
                indicatorName: this.visualization.props.name,
                companyName: this.company.props.name,
                isLike: true,
                toUserId: this.report.props.userId,
                isGroup: this.company.isGroup
            });

            // Cria o post entity para o like
            const likePost = PostEntity.restore({
                _id: postId,
                comment: {
                    msg: option.label,
                    t: new Date().toISOString(),
                    optionLike: option.value
                },
                u: { _id: this.contact.props._id, n: this.contact.props.name },
                ac: this.company.props.accountingId,
                c: this.company.props._id,
                v: this.visualization.props._id,
                t: new Date().toISOString(),
                group: [],
                isLike: true
            });

            // Atualiza os likes da visualização
            const updatedLikes = this.visualization.postsLike
                ? this.visualization.postsLike.filter(
                      (like) => like.props.u._id !== this.contact.props._id
                  )
                : [];
            updatedLikes.push(likePost);

            // Passa todos os posts (normais + likes) para setPosts fazer a separação correta
            const allPosts = [...this.visualization.posts, ...updatedLikes];
            this.visualization.setPosts(allPosts);

            // Verifica novamente o like do usuário atual e atualiza o estado
            this.checkCurrentUserLike();
            this.updateOptionsState();

            this.cdk.detectChanges();
        } catch (error) {
            console.error("Erro ao processar like:", error);
        }
    }

    public async onSendComment(commentValue: string): Promise<void> {
        if (!commentValue) return;
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.comentar`
        );
        try {
            const comment = structuredClone(commentValue);
            this.commentForm.reset();
            this.loadingComment = true;
            const postId = GeneratorIdUtils.execute();
            this.insertPostController.execute({
                _id: postId,
                accountingId: this.company.props.accountingId,
                companyId: this.company.props._id,
                comment: { msg: comment, t: new Date() },
                scenarioId: this.options.scenario.props._id,
                periodDate: this.options.endDate,
                user: {
                    _id: this.contact.props._id,
                    n: this.contact.props.name,
                    isContact: this.contact.isContact
                },
                visualizationId: this.visualization.props._id,
                reportId: this.report.props._id,
                reportName: this.report.props.name,
                indicatorName: this.visualization.props.name,
                companyName: this.company.props.name,
                isLike: false,
                toUserId: this.report.props.userId,
                isGroup: this.company.isGroup
            });
            const post = PostEntity.restore({
                _id: postId,
                comment: { msg: comment, t: new Date().toISOString() },
                u: { _id: this.contact.props._id, n: this.contact.props.name },
                ac: this.company.props.accountingId,
                c: this.company.props._id,
                v: this.visualization.props._id,
                t: new Date().toISOString(),
                group: []
            });
            commentValue = "";

            this.visualization.setPosts([...this.visualization.posts, post]);
            this.modelerPosts();
            this.updateMinHeight();

            this.loadingComment = false;
        } catch (error) {
            console.log("error", error);
        } finally {
            this.cdk.detectChanges();
            this.scrollToBottom();
        }
    }
}
